import React, { useEffect } from 'react';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import config from 'react-global-configuration';
import type { ImmutableRootState } from '@uncinc/react-drupal-core/types';
import * as Yup from 'yup';
import {
  FormikWrapper,
  FormTextField,
  Button,
  Container,
} from '@uncinc/uncinc-react-kitchen-sink';

import { DOMAIN_KVNL } from '../../config/constants';

import { ROUTE_NEWSLETTER_SUBSCRIBE, ROUTE_NEWSLETTER_SETTINGS } from '../../config/routes';
import useCurrentUser from '../../hooks/useCurrentUser';
import {
  getNewsletterContact,
} from '../../store/actions/UI';

import './style.scss';

const NewsletterBlock: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const user = useCurrentUser();
  const loggedIn = useSelector<ImmutableRootState<boolean>, boolean>((state) => state.getIn(['login', 'loggedIn'], undefined));
  const spotlerUserContact = useSelector<ImmutableRootState<boolean>, boolean>((state) => state.getIn(['uiInternal', 'newsletter', 'contact'], undefined));
  const spotlerUserContactSubscribed = useSelector<ImmutableRootState<boolean>, boolean>((state) => state.getIn(['uiInternal', 'newsletter', 'contact', 'is_subscribed'], undefined));

  const validationSchema = Yup.object({
    email: Yup.string()
      .email(t('form.register.email.invalid'))
      .required(t('form.register.email.required')),
  });

  // Redirects the user to the correct page.
  const onSubmit = (values: any) => {
    let destinationUrl = ROUTE_NEWSLETTER_SUBSCRIBE;
    if (loggedIn && spotlerUserContactSubscribed) {
      // We already have a spotler subscription.
      destinationUrl = ROUTE_NEWSLETTER_SETTINGS;
    } else {
      // Add our email input to the subscription page.
      destinationUrl = `${destinationUrl}?email=${encodeURIComponent(values.email)}`;
    }
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });

    setTimeout(() => {
      history.push(destinationUrl);
    }, 300);
  };

  // Try to fetch our Spotler account if we are logged in.
  useEffect(() => {
    if (loggedIn && !spotlerUserContact) {
      dispatch(getNewsletterContact(user?.mail?.[0].value) as any);
    }
  }, [loggedIn, spotlerUserContact, getNewsletterContact, dispatch]);

  const domainIsKVNL = config.get('CURRENT_DOMAIN') === DOMAIN_KVNL;
  if (domainIsKVNL) return null;

  return (
    <div
      className="NewsletterBlock"
    >
      <Container>
        <div className="row">
          <div className={`col-12 ${loggedIn ? 'col-lg-9' : 'col-lg-5'}`}>
            <div className="NewsletterBlock__body">
              {(loggedIn && spotlerUserContactSubscribed) ? (
                <p>{t('spotler.maintain_preferences')}</p>
              ) : (
                <p>{t('spotler.subscribe_notice')}</p>
              )}
            </div>
          </div>
          <div className={`col-12 ${loggedIn ? 'col-lg-3' : 'col-lg-7'}`}>
            <FormikWrapper
              validationSchema={!loggedIn ? validationSchema : null}
              onSubmit={onSubmit}
            >
              <div className="NewsletterBlock__actions">
                {!loggedIn && (
                  <FormTextField
                    fieldName="email"
                    type="email"
                    required
                    data-lpignore
                    autocomplete="off"
                    placeholder={t('login.email.label')}
                  />
                )}
                <Button type="submit">
                  {(loggedIn && spotlerUserContact !== null) ? (
                    <span>{t('reset_password.button')}</span>
                  ) : (
                    <span>{t('spotler.subscribe')}</span>
                  )}
                  <i className="icon icon-arrow-right" />
                </Button>
              </div>

            </FormikWrapper>

          </div>
        </div>
      </Container>
    </div>
  );
};

export default NewsletterBlock;
